import { useMemo, useRef, useState } from 'react';
import {
  portalWiQuote, apiErrText, countdown, wiRate, wiMoney,
  type PortalWiDetail, type PortalWiQuoteLine,
} from '../lib/api';
import { toast } from './ui/Toast';

/**
 * 9.1.3 乙方报价 — the contractor's quote for one 变更工程指令. Eric 9/18 (工程指令).
 *
 * §8 risk 4 is the reason this screen exists in the shape it does: the portal is not yet the contractual
 * notice channel, so nothing here may LOOK like an enforcement. What it can do is make the signed 指令单
 * faster to get than to skip — one screen, the rates pre-filled from last time, the camera one tap away,
 * and the 7-day clock and the clause it rests on shown together so neither side can later claim surprise.
 *
 * Three rules the layout encodes:
 *   · 工程量 and 单位 are READ-ONLY. They come off the signed order; a contractor who disagrees says so in
 *     备注 and WTL re-issues — silently re-quoting a different quantity is how a 结算 dispute starts.
 *   · 合价 is computed in front of them (单价 × 工程量) and sent as a courtesy only: 预算 recomputes
 *     everything in PR3 and the server's arithmetic wins (G9).
 *   · A forfeited order is a RED card with a disabled form — unless the order was served after its own
 *     deadline (`late_service`), in which case the clock was never theirs to miss and the form stays open
 *     with one line saying why.
 */

/** Mirrors the server caps (routes/instructionEvidence.ts MAX_EVIDENCE_FILES / EVIDENCE_LIMIT_MB). */
export const WI_MAX_FILES = 10;
export const WI_MAX_FILE_BYTES = 30 * 1024 * 1024;
const ACCEPT_EVIDENCE = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif';

/** 9.1.3, verbatim. Never paraphrased — it is a contract quotation, and it is the whole point of the clock. */
export const QUOTE_CLAUSE_ZH = '逾期未报，工程量不应计量、费用不应补偿。';

/**
 * Pick-then-confirm photo picker (fb_ac916632, 江卫春): choosing a file never uploads it. Two inputs on
 * purpose — 📎 for an image already on the phone, 📷 (`capture="environment"`) for the wall in front of them.
 * Shared by the quote form, the 计日工 daily form and 返工完成 so the gesture is identical everywhere.
 */
export function PhotoPicker({
  picked, onPicked, disabled, idPrefix, hint,
}: {
  picked: File[];
  onPicked: (files: File[]) => void;
  disabled?: boolean;
  idPrefix: string;
  hint?: string;
}) {
  const pickRef = useRef<HTMLInputElement>(null);
  const camRef = useRef<HTMLInputElement>(null);
  const [err, setErr] = useState<string | null>(null);

  function clearPick() {
    onPicked([]); setErr(null);
    if (pickRef.current) pickRef.current.value = '';
    if (camRef.current) camRef.current.value = '';
  }
  /** Accumulate across both inputs — 拍照 twice then attach a PDF is a normal sequence on a site phone. */
  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const added = [...(e.target.files ?? [])];
    if (!added.length) return;
    const next = [...picked, ...added];
    if (next.length > WI_MAX_FILES) {
      setErr(`一次最多上传 ${WI_MAX_FILES} 个文件，请分批上传。/ At most ${WI_MAX_FILES} files per upload — send them in batches.`);
      e.target.value = '';
      return;
    }
    const tooBig = next.find((f) => f.size > WI_MAX_FILE_BYTES);
    if (tooBig) {
      setErr(`文件「${tooBig.name}」超过 30MB 上限，请压缩后再上传。/ 「${tooBig.name}」 exceeds the 30MB limit — compress it.`);
      e.target.value = '';
      return;
    }
    setErr(null);
    onPicked(next);
    e.target.value = '';   // so picking the SAME file again still fires onChange
  }

  return (
    <div style={{ marginTop: 6 }}>
      <div className="row-wrap" style={{ gap: 10, alignItems: 'center' }}>
        <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>📎 选择照片 / Choose photos</span>
          <input ref={pickRef} id={`${idPrefix}-pick`} type="file" multiple accept={ACCEPT_EVIDENCE} onChange={onPick} disabled={disabled} />
        </label>
        <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>📷 现场拍照 / Take a photo</span>
          <input ref={camRef} id={`${idPrefix}-cam`} type="file" accept="image/*" capture="environment" onChange={onPick} disabled={disabled} />
        </label>
      </div>
      {hint && <div className="muted small" style={{ marginTop: 2 }}>{hint}</div>}
      {picked.length > 0 && (
        <div className="row-wrap" style={{ gap: 6, alignItems: 'center', marginTop: 4 }}>
          <span className="small muted" style={{ flex: 1, minWidth: 140, wordBreak: 'break-word' }}>
            待上传 / to upload（{picked.length}）：{picked.map((f) => f.name).join('、')}
          </span>
          <button className="small" disabled={disabled} onClick={clearPick}>清空 / Clear</button>
        </div>
      )}
      {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 4 }}>{err}</div>}
    </div>
  );
}

interface RowState { rate: string; note: string }

export default function PortalQuoteForm({
  eid, detail, onUpdated,
}: {
  eid: string;
  detail: PortalWiDetail;
  onUpdated: (d: PortalWiDetail) => void;
}) {
  const wi = detail.instruction;
  const [rows, setRows] = useState<Record<string, RowState>>(() => {
    const init: Record<string, RowState> = {};
    for (const l of detail.lines) {
      init[l.id] = {
        rate: l.declared_unit_rate_rmb == null ? '' : String(l.declared_unit_rate_rmb),
        note: l.declared_note_md ?? '',
      };
    }
    return init;
  });
  const [picked, setPicked] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const evidenceOnFile = useMemo(
    () => detail.photos.filter((p) => p.kind === 'quote_evidence').length,
    [detail.photos],
  );
  // The clock stops once quoted or 失权 — hours_left is null then, and there is nothing left to count to.
  const cd = countdown(detail.quote_due_at);
  const showClock = detail.hours_left != null && !!detail.quote_due_at;
  // G13: a right that was never theirs to miss. Forfeiture is suppressed on a late-served order, and the
  // form stays open — with the reason on screen, because a red card with a live button is confusing.
  const blocked = detail.forfeited && !detail.late_service;
  const resubmitting = wi.state === 'quoted';
  const photoOwed = detail.photo_required_on_quote && evidenceOnFile === 0 && picked.length === 0;

  const total = useMemo(() => {
    let sum = 0; let any = false;
    for (const l of detail.lines) {
      const r = Number(rows[l.id]?.rate);
      if (Number.isFinite(r) && rows[l.id]?.rate !== '' && l.qty != null) { sum += r * l.qty; any = true; }
    }
    return any ? Math.round(sum * 100) / 100 : null;
  }, [rows, detail.lines]);

  const incomplete = detail.lines.some((l) => {
    const v = rows[l.id]?.rate ?? '';
    return v.trim() === '' || !Number.isFinite(Number(v)) || Number(v) < 0;
  });

  async function submit() {
    if (incomplete || photoOwed) return;
    setBusy(true); setErr(null);
    try {
      const lines: PortalWiQuoteLine[] = detail.lines.map((l) => {
        const rate = Number(rows[l.id].rate);
        return {
          line_id: l.id,
          declared_unit_rate_rmb: rate,
          // courtesy only — 预算 recomputes and the server's arithmetic wins (G9)
          declared_amount_rmb: l.qty == null ? null : Math.round(rate * l.qty * 100) / 100,
          declared_note_md: rows[l.id].note.trim() || null,
        };
      });
      const updated = await portalWiQuote(eid, wi.id, lines, picked);
      onUpdated(updated);
      setPicked([]);
      toast('报价已提交，等待 WTL 审核。/ Quote submitted — WTL will review it.', { kind: 'success' });
    } catch (e) {
      setErr(apiErrText(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ marginTop: 10, borderLeft: `3px solid ${blocked ? 'var(--bad)' : 'var(--accent)'}` }}>
      <div className="row-wrap" style={{ gap: 8, alignItems: 'baseline' }}>
        <strong style={{ flex: 1, minWidth: 140 }}>我的报价 / My quote</strong>
        {resubmitting && <span className="pill small pill-stage">已报价，可修改 / Quoted — you may revise it</span>}
      </div>

      {/* ── the clock, and the sentence it rests on ─────────────────────────────────────────────── */}
      {showClock && (
        <div className="row-wrap" style={{ gap: 6, alignItems: 'baseline', marginTop: 4 }}>
          <span className="small">报价期限 / Quote due：{String(detail.quote_due_at).slice(0, 16).replace('T', ' ')}</span>
          <span className={`pill small ${cd.expired ? 'pill-bad' : cd.urgent ? 'pill-bad' : 'pill-warn'}`}>{cd.text}</span>
        </div>
      )}
      {(showClock || blocked) && (
        <div className="muted small" style={{ marginTop: 2 }}>
          合同 9.1.3：{QUOTE_CLAUSE_ZH} / Clause 9.1.3 — a quote filed after the window is not measured and not compensated.
        </div>
      )}

      {blocked && (
        <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>
          <strong>⛔ 逾期失权 / Time-barred</strong> — 本指令的报价期限已过（{String(detail.quote_due_at ?? '').slice(0, 10)}），
          报价通道已关闭。如有异议请在「沟通」留言联系 WTL；以合同约定为准。
          <div className="muted" style={{ marginTop: 2 }}>
            The 7-day quote window has closed and this form is disabled — raise it with WTL in the message
            thread. The contract governs.
          </div>
        </div>
      )}
      {detail.forfeited && detail.late_service && (
        <div className="small" style={{ color: 'var(--good)', marginTop: 6 }}>
          ✓ 本指令送达时已超过报价期限，报价通道仍为您保留。/ This order was served after its own deadline, so
          the window was never yours to miss — the form stays open.
        </div>
      )}

      {/* ── per-line 申报单价 ───────────────────────────────────────────────────────────────────── */}
      <div style={{ marginTop: 8 }}>
        {detail.lines.map((l, i) => {
          const v = rows[l.id] ?? { rate: '', note: '' };
          const r = Number(v.rate);
          const amount = v.rate !== '' && Number.isFinite(r) && l.qty != null ? Math.round(r * l.qty * 100) / 100 : null;
          return (
            <div key={l.id} style={{ padding: '8px 0', borderTop: '1px solid var(--rule)' }}>
              <div className="small"><strong>{i + 1}. {l.item_label}</strong></div>
              {l.spec_md && <div className="muted small">{l.spec_md}</div>}
              <div className="muted small" style={{ marginTop: 2 }}>
                工程量 / Qty：<strong>{l.qty ?? '—'}</strong> {l.unit ?? ''}
                {l.measurement_calc_md && <> · 计算式 / calc：{l.measurement_calc_md}</>}
              </div>
              {/* A time-barred order shows the line, never an input: a disabled field on this theme is
                  indistinguishable from a live one, and inviting a rate nobody may submit is worse than
                  saying plainly that the channel is closed. */}
              {blocked ? (
                <div className="muted small" style={{ marginTop: 4 }}>
                  报价通道已关闭 / quoting is closed on this line
                  {l.declared_unit_rate_rmb != null && <> · 上次申报 / last declared {wiRate(l.declared_unit_rate_rmb)}</>}
                </div>
              ) : (
                <>
                  <div className="row-wrap" style={{ gap: 6, alignItems: 'center', marginTop: 4 }}>
                    <label className="small" htmlFor={`wq-rate-${l.id}`} style={{ minWidth: 110 }}>申报单价 / Unit rate ¥</label>
                    <input
                      id={`wq-rate-${l.id}`} type="number" inputMode="decimal" min={0} step="0.01"
                      value={v.rate} disabled={busy}
                      onChange={(e) => setRows({ ...rows, [l.id]: { ...v, rate: e.target.value } })}
                      style={{ width: 120 }}
                    />
                    <span className="small muted">合价 / Amount：<strong>{amount == null ? '—' : wiMoney(amount)}</strong></span>
                  </div>
                  <div className="row-wrap" style={{ gap: 6, alignItems: 'center', marginTop: 4 }}>
                    <input
                      className="grow" style={{ minWidth: 140 }} value={v.note} disabled={busy}
                      onChange={(e) => setRows({ ...rows, [l.id]: { ...v, note: e.target.value } })}
                      placeholder="备注（选填）/ note (optional)"
                    />
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {!blocked && (
        <>
          <div className="row-wrap" style={{ gap: 8, alignItems: 'baseline', marginTop: 6, borderTop: '1px solid var(--rule)', paddingTop: 6 }}>
            <span className="small" style={{ flex: 1, minWidth: 120 }}>申报合计 / Declared total</span>
            <strong className="money-lg" style={{ fontSize: 16 }}>{total == null ? '—' : wiMoney(total)}</strong>
          </div>
          <div className="muted small">
            合价为按单价 × 工程量自动计算，最终以 WTL 审核结果为准。/ Amounts are rate × quantity; WTL's audited
            figure governs.
          </div>
        </>
      )}

      {/* ── 现场影像 ───────────────────────────────────────────────────────────────────────────── */}
      {!blocked && (
        <>
          <div className="small" style={{ marginTop: 8 }}>
            <strong>现场影像 / Site photos</strong>
            {detail.photo_required_on_quote && <span className="pill small pill-bad" style={{ marginLeft: 6 }}>必填 / Required</span>}
            {evidenceOnFile > 0 && <span className="pill small pill-good" style={{ marginLeft: 6 }}>已有 {evidenceOnFile} 张 / {evidenceOnFile} on file</span>}
          </div>
          <PhotoPicker
            picked={picked} onPicked={setPicked} disabled={busy} idPrefix={`wq-${wi.id}`}
            hint="请拍摄工作面现状与已完成部位，作为工程量与计价依据。/ Photograph the work face — it is the evidence behind the quantity."
          />
          {photoOwed && (
            <div className="small" style={{ color: 'var(--bad)', marginTop: 4 }}>
              请先附现场照片再提交（合同 9.1.3）。/ Attach a site photo before submitting.
            </div>
          )}
        </>
      )}

      {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>{err}</div>}

      {!blocked && (
        <div className="row-wrap" style={{ gap: 6, alignItems: 'center', marginTop: 8 }}>
          <button className="btn-good" disabled={busy || incomplete || photoOwed} onClick={submit}>
            {busy ? '提交中… / submitting…' : resubmitting ? '✓ 更新报价 / Update quote' : '✓ 提交报价 / Submit quote'}
          </button>
          {incomplete && <span className="muted small">请为每一行填写单价。/ Every line needs a unit rate.</span>}
        </div>
      )}

      {/* 申报 vs 审核, once 预算 has priced it — the steps live in the 认价单 card below. */}
      {['approved', 'confirmed', 'disputed'].includes(detail.instruction.state) && detail.lines.some((l) => l.audited_unit_rate_rmb != null) && (
        <div className="muted small" style={{ marginTop: 8 }}>
          WTL 审核单价已出，详见下方「认价单」。/ WTL has audited these rates — see the 认价单 card below.
          {detail.lines.map((l, i) => (
            <div key={l.id}>
              {i + 1}. {l.item_label}：申报 {wiRate(l.declared_unit_rate_rmb)} → 审核 {wiRate(l.audited_unit_rate_rmb)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
